<template>
  <section class="pj" aria-labelledby="pj-heading">
    <h2 id="pj-heading" class="pj-title">Your path</h2>
    <p class="pj-lead">Everything on this path, in order. Each step opens where the work happens.</p>

    <ol class="pj-line">
      <!-- 1. Certification: the school where the path starts -->
      <li class="pj-step" :class="`is-${steps.cert}`">
        <span class="pj-dot" aria-hidden="true"><svg v-if="steps.cert === 'done'" viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5" /></svg></span>
        <div class="pj-card">
          <div class="pj-head">
            <span class="pj-num">Step 1</span>
            <span class="pj-status">{{ statusLabel(steps.cert) }}</span>
          </div>
          <h3 class="pj-h">My certification</h3>
          <div class="pj-school">
            <span class="pj-logo"><img v-if="schoolLogo" :src="schoolLogo" alt="" /><span v-else>{{ initial(schoolName) }}</span></span>
            <div class="pj-school-text">
              <b>{{ fromLabel }}</b>
              <small>{{ schoolName }}</small>
            </div>
          </div>
          <button type="button" class="pj-btn" @click="$emit('open-school')">Go to my school<svg viewBox="0 0 24 24"><path d="m9 5 7 7-7 7" /></svg></button>
        </div>
      </li>

      <!-- 2. Programs: the student's applications, top 10 -->
      <li class="pj-step" :class="`is-${steps.programs}`">
        <span class="pj-dot" aria-hidden="true"><svg v-if="steps.programs === 'done'" viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5" /></svg></span>
        <div class="pj-card">
          <div class="pj-head">
            <span class="pj-num">Step 2</span>
            <span class="pj-status">{{ statusLabel(steps.programs) }}</span>
          </div>
          <h3 class="pj-h">My {{ toLabel }} programs <span v-if="programs.length" class="pj-count">{{ programs.length }}</span></h3>
          <ul v-if="programs.length" class="pj-apps">
            <li v-for="p in shownPrograms" :key="p.key">
              <router-link :to="p.to" class="pj-app">
                <span class="pj-logo pj-logo--sm"><img v-if="p.logo" :src="p.logo" alt="" /><span v-else>{{ initial(p.name) }}</span></span>
                <span class="pj-app-text">
                  <b>{{ p.name }}</b><small v-if="p.location">{{ p.location }}</small>
                  <span class="pj-app-state" :class="appStateClass(p)">{{ appStateLabel(p) }}</span>
                </span>
                <span v-if="p.score != null" class="pj-score" :class="scoreClass(p.score)" :aria-label="`Score ${p.score} of 100`"><b>{{ p.score }}</b><small>Score</small></span>
              </router-link>
            </li>
          </ul>
          <p v-else class="pj-empty">Your school adds the programs you’re applying to.</p>
          <button v-if="programs.length > 10" type="button" class="pj-more" @click="showAll = !showAll">
            {{ showAll ? 'Show fewer' : `View all ${programs.length}` }}
            <svg :class="{ 'is-up': showAll }" viewBox="0 0 24 24"><path d="m5 9 7 7 7-7" /></svg>
          </button>
        </div>
      </li>

      <!-- 3. Prereqs: the courses and admission exams programs of this kind ask for -->
      <li class="pj-step" :class="`is-${steps.prereqs}`">
        <span class="pj-dot" aria-hidden="true"><svg v-if="steps.prereqs === 'done'" viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5" /></svg></span>
        <div class="pj-card">
          <div class="pj-head">
            <span class="pj-num">Step 3</span>
            <span class="pj-status">{{ statusLabel(steps.prereqs) }}</span>
          </div>
          <h3 class="pj-h">My prereqs</h3>
          <template v-if="prereqGroups.length">
            <div class="pj-prq-cols">
              <div v-for="g in prereqGroups" :key="g.key" class="pj-prq-col">
                <h4 class="pj-prq-title">{{ g.key === 'entrance_exam' ? 'Admission exams' : g.label }} <span class="pj-count">{{ g.items.length }}</span></h4>
                <ul class="pj-prq-rows">
                  <li v-for="item in (prqAll ? g.items : g.items.slice(0, prqLimit(g)))" :key="item.key">
                    <button type="button" class="pj-prq-row" @click="$emit('open-prereq', item, g.key)">
                      <span class="pj-prq-ico" aria-hidden="true"><svg viewBox="0 0 256 256" fill="currentColor" v-html="prereqIconSvg(item.key, g.key)" /></span>
                      <b>{{ item.label }}</b>
                      <span class="pj-prq-pct">{{ Math.max(1, Math.round(item.share * 100)) }}%</span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <p class="pj-note">% of {{ toLabel }} programs that ask for it.</p>
            <button v-if="prqHidden" type="button" class="pj-more" @click="prqAll = !prqAll">
              {{ prqAll ? 'Show fewer' : `View all ${prqTotal}` }}
              <svg :class="{ 'is-up': prqAll }" viewBox="0 0 24 24"><path d="m5 9 7 7 7-7" /></svg>
            </button>
          </template>
          <p v-else class="pj-empty">The courses and exams {{ toLabel }} programs ask for appear here.</p>
        </div>
      </li>

      <!-- 4. Mentor -->
      <li class="pj-step" :class="`is-${steps.mentor}`">
        <span class="pj-dot" aria-hidden="true"><svg v-if="steps.mentor === 'done'" viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5" /></svg></span>
        <div class="pj-card">
          <div class="pj-head">
            <span class="pj-num">Step 4</span>
            <span class="pj-status">{{ mentor ? 'Connected' : statusLabel(steps.mentor) }}</span>
          </div>
          <h3 class="pj-h">My mentor</h3>
          <div v-if="mentor" class="pj-school">
            <span class="pj-logo pj-logo--round"><img v-if="mentor.image" :src="mentor.image" alt="" /><span v-else>{{ initial(mentor.name) }}</span></span>
            <div class="pj-school-text"><b>{{ mentor.name }}</b><small v-if="mentor.blurb">{{ mentor.blurb }}</small></div>
          </div>
          <p v-else class="pj-empty">Your mentor will appear here once one is matched to your path.</p>
        </div>
      </li>
    </ol>
  </section>
</template>

<script>
/**
 * The enrolled student's path as one vertical line: certification, programs, prereqs, mentor.
 * Each step is a card on the line with a status dot (done, current, next).
 */
import { prereqIconSvg } from '@/tu/utils/prereqIcons';

export default {
  name: 'PathJourney',
  props: {
    schoolName: { type: String, default: '' },
    schoolLogo: { type: String, default: '' },
    fromLabel: { type: String, default: 'Your first program' },
    toLabel: { type: String, default: '' },
    /** [{ key, name, logo, location, percent, completed, total, score, to }], best score first */
    programs: { type: Array, default: () => [] },
    /** { name, image, blurb } or null */
    mentor: { type: Object, default: null },
    /** The prerequisite summary for the destination program ({ offerings, groups }) */
    prereqSummary: { type: Object, default: null },
  },
  data() {
    return { showAll: false, prqAll: false };
  },
  computed: {
    shownPrograms() {
      return this.showAll ? this.programs : this.programs.slice(0, 10);
    },
    /** Courses and admission exams only (ACT or SAT left out), most asked for first. */
    prereqGroups() {
      const groups = (this.prereqSummary && this.prereqSummary.groups) || [];
      return groups
        .filter(g => g.key === 'college_course' || g.key === 'entrance_exam')
        .map(g => ({ ...g, items: g.items.filter(i => i.key !== 'act_sat') }))
        .filter(g => g.items.length);
    },
    prqTotal() {
      return this.prereqGroups.reduce((n, g) => n + g.items.length, 0);
    },
    prqHidden() {
      return this.prereqGroups.some(g => g.items.length > this.prqLimit(g));
    },
    /** Certification is under way once enrolled; the rest follow in order. */
    steps() {
      const programsDone = this.programs.length > 0 && this.programs.every(p => Number(p.percent) >= 100);
      return {
        cert: 'current',
        programs: programsDone ? 'done' : this.programs.length ? 'current' : 'next',
        prereqs: 'next',
        mentor: this.mentor ? 'done' : 'next',
      };
    },
  },
  methods: {
    prereqIconSvg,
    /** Top 5 courses and top 3 exams, like the public path page. */
    prqLimit(g) {
      return g.key === 'entrance_exam' ? 3 : 5;
    },
    initial(name) {
      return String(name || '?').trim().charAt(0).toUpperCase();
    },
    statusLabel(s) {
      return { done: 'Complete', current: 'In progress', next: 'Up next' }[s] || '';
    },
    appStateLabel(p) {
      const pct = Number(p.percent) || 0;
      if (pct >= 100) return 'Ready to apply';
      if (pct > 0) return `${pct}% ready`;
      return 'Not started';
    },
    scoreClass(score) {
      return score >= 85 ? 'is-high' : score >= 70 ? 'is-mid' : '';
    },
    appStateClass(p) {
      const pct = Number(p.percent) || 0;
      return pct >= 100 ? 'is-ready' : pct > 0 ? 'is-going' : '';
    },
  },
};
</script>

<style scoped>
.pj { max-width: 820px; margin: 8px 0 48px; }
.pj-title { margin: 0; font-family: var(--tu-font-display, inherit); font-size: 30px; font-weight: 800; letter-spacing: -0.02em; color: var(--tu-ink, #181818); }
.pj-lead { margin: 8px 0 28px; font-size: 16px; color: var(--tu-ink-soft, #5f6b80); }

.pj-line { list-style: none; margin: 0; padding: 0; }
.pj-step { position: relative; padding: 0 0 22px 52px; }
/* the line between dots */
.pj-step::before { content: ''; position: absolute; left: 15px; top: 34px; bottom: -2px; width: 2px; background: var(--tu-line, #e2e8f0); }
.pj-step:last-child::before { display: none; }
.pj-step.is-done::before { background: var(--tu-blue, #3b86f5); }

.pj-dot { position: absolute; left: 0; top: 18px; width: 32px; height: 32px; border-radius: 50%; box-sizing: border-box; display: grid; place-items: center; background: #fff; border: 2px solid var(--tu-line-strong, #cfd6e1); }
.pj-dot svg { width: 16px; height: 16px; fill: none; stroke: #fff; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.pj-step.is-done .pj-dot { background: var(--tu-blue, #3b86f5); border-color: var(--tu-blue, #3b86f5); }
.pj-step.is-current .pj-dot { border-color: var(--tu-blue, #3b86f5); box-shadow: inset 0 0 0 6px #fff, inset 0 0 0 16px var(--tu-blue, #3b86f5); }

.pj-card { background: #fff; border: 1px solid var(--tu-line, #e2e8f0); border-radius: 18px; padding: 20px 22px 22px; box-shadow: 0 1px 2px rgba(24, 24, 24, 0.04); }
.pj-step.is-current .pj-card { border-color: #b9cef2; }
.pj-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.pj-num { font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--tu-blue, #3b86f5); }
.pj-status { padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; background: var(--tu-surface-2, #f3f5f8); color: var(--tu-ink-soft, #5f6b80); }
.pj-step.is-done .pj-status { background: #e7f6ee; color: #15803d; }
.pj-step.is-current .pj-status { background: var(--tu-blue-tint, #e8f0fe); color: var(--tu-blue-deep, #2f73e0); }
.pj-h { margin: 10px 0 14px; font-size: 20px; font-weight: 700; color: var(--tu-ink, #181818); display: flex; align-items: center; gap: 8px; }
.pj-count { padding: 2px 9px; border-radius: 999px; background: var(--tu-blue-tint, #e8f0fe); color: var(--tu-blue-deep, #2f73e0); font-size: 13px; }

.pj-school { display: flex; align-items: center; gap: 14px; }
.pj-logo { flex: none; width: 48px; height: 48px; border-radius: 12px; border: 1px solid var(--tu-line, #e2e8f0); background: #fff; display: grid; place-items: center; overflow: hidden; font-weight: 800; color: var(--tu-blue, #3b86f5); }
.pj-logo img { width: 100%; height: 100%; object-fit: contain; padding: 4px; box-sizing: border-box; }
.pj-logo--sm { width: 38px; height: 38px; border-radius: 10px; font-size: 14px; }
.pj-logo--round { border-radius: 50%; }
.pj-logo--round img { object-fit: cover; padding: 0; }
.pj-school-text { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pj-school-text b { font-size: 16px; color: var(--tu-ink, #181818); }
.pj-school-text small { font-size: 14px; color: var(--tu-ink-soft, #5f6b80); }

.pj-btn { display: inline-flex; align-items: center; gap: 8px; margin-top: 16px; padding: 10px 16px; border: 0; border-radius: 12px; background: var(--tu-blue, #3b86f5); color: #fff !important; font-size: 15px; font-weight: 700; text-decoration: none !important; cursor: pointer; }
.pj-btn:hover { background: var(--tu-blue-deep, #2f73e0); }
.pj-btn svg, .pj-more svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

.pj-apps { list-style: none; margin: 0; padding: 0; border: 1px solid var(--tu-line, #e2e8f0); border-radius: 14px; overflow: hidden; }
.pj-apps li + li { border-top: 1px solid var(--tu-line, #e2e8f0); }
.pj-app { display: flex; align-items: center; gap: 12px; padding: 12px 14px; color: inherit !important; text-decoration: none !important; }
.pj-app:hover { background: var(--tu-surface-2, #f7f9fc); }
.pj-app-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.pj-app-text b { font-size: 15px; color: var(--tu-ink, #181818); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pj-app-text small { font-size: 13px; color: var(--tu-ink-soft, #5f6b80); }
.pj-app-state { align-self: flex-start; margin-top: 6px; padding: 3px 9px; border-radius: 999px; font-size: 12.5px; font-weight: 700; background: var(--tu-surface-2, #f3f5f8); color: var(--tu-ink-soft, #5f6b80); }
.pj-app-state.is-going { background: var(--tu-blue-tint, #e8f0fe); color: var(--tu-blue-deep, #2f73e0); }
.pj-app-state.is-ready { background: #e7f6ee; color: #15803d; }
/* program score: the number the cards are ranked by */
.pj-score { flex: none; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 58px; height: 58px; border-radius: 14px; background: var(--tu-surface-2, #f3f5f8); color: var(--tu-ink, #181818); }
.pj-score b { font-size: 21px; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
.pj-score small { margin-top: 3px; font-size: 10.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--tu-ink-soft, #5f6b80); }
.pj-score.is-mid { background: var(--tu-blue-tint, #e8f0fe); color: var(--tu-blue-deep, #2f73e0); }
.pj-score.is-high { background: var(--tu-blue, #3b86f5); color: #fff; }
.pj-score.is-high small { color: rgba(255, 255, 255, .85); }

.pj-more { display: inline-flex; align-items: center; gap: 8px; margin-top: 14px; padding: 10px 18px; border: 0; border-radius: 14px; background: #fff; box-shadow: 0 1px 2px rgba(24, 24, 24, 0.06); color: var(--tu-blue, #3b86f5); font-size: 15px; font-weight: 600; cursor: pointer; }
.pj-more svg { transition: transform 0.2s ease; }
.pj-more svg.is-up { transform: rotate(180deg); }

/* prereqs: courses and admission exams side by side, one framed list each */
.pj-prq-cols { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 20px; align-items: start; }
.pj-prq-title { display: flex; align-items: center; gap: 8px; margin: 0 0 10px; font-size: 15px; font-weight: 700; color: var(--tu-ink, #181818); }
.pj-prq-rows { list-style: none; margin: 0; padding: 2px 14px; border: 1px solid var(--tu-line, #e2e8f0); border-radius: 14px; background: #fff; }
.pj-prq-rows li + li { border-top: 1px solid var(--tu-line, #e2e8f0); }
.pj-prq-row { display: flex; align-items: center; gap: 12px; width: 100%; padding: 11px 0; border: 0; background: transparent; text-align: left; font: inherit; cursor: pointer; }
.pj-prq-row b { flex: 1; min-width: 0; font-size: 14.5px; font-weight: 600; color: var(--tu-ink, #181818); }
.pj-prq-row:hover b { color: var(--tu-blue-deep, #2f73e0); }
.pj-prq-ico { display: grid; place-items: center; flex: none; width: 32px; height: 32px; border-radius: 10px; background: var(--tu-blue-tint, #e8f0fe); color: var(--tu-blue-deep, #2f73e0); }
.pj-prq-ico svg { width: 17px; height: 17px; }
.pj-prq-pct { flex: none; font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; color: var(--tu-blue-deep, #2f73e0); }
.pj-note { margin: 10px 0 0; font-size: 14.5px; color: var(--tu-ink-soft, #5f6b80); }
.pj-note b { color: var(--tu-ink, #181818); }
.pj-empty { margin: 0; font-size: 15px; color: var(--tu-ink-soft, #5f6b80); }

@media (max-width: 600px) {
  .pj-prq-cols { grid-template-columns: 1fr; }
  .pj-step { padding-left: 42px; }
  .pj-dot { width: 28px; height: 28px; top: 16px; }
  .pj-step::before { left: 13px; top: 30px; }
  .pj-card { padding: 16px 16px 18px; }
  .pj-h { font-size: 18px; }
  .pj-app { align-items: flex-start; }
  .pj-app-text b { white-space: normal; }
  .pj-score { width: 52px; height: 52px; }
}
</style>
